/**
 * Template Library — admin-only browsable gallery of meeting-rule templates.
 * Combines built-in Ragnes presets with every custom template the admin can
 * see (their own drafts + all published templates across the network).
 * Search + category filter + apply-to-any-workspace-you-admin flow.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles, Search, Layers, Globe, Lock, Users, Download, ArrowRight, X, Loader2, Star,
} from "lucide-react";

const KIND_COLORS = {
  focus: "bg-emerald-500/15 text-emerald-500",
  one_on_one: "bg-amber-500/15 text-amber-500",
  sprint: "bg-sky-500/15 text-sky-500",
  review: "bg-fuchsia-500/15 text-fuchsia-500",
  client: "bg-rose-500/15 text-rose-500",
  interview: "bg-violet-500/15 text-violet-500",
  meeting: "bg-zinc-500/15 text-zinc-500",
  ooo: "bg-slate-500/15 text-slate-500",
};

export default function TemplateLibrary() {
  const { workspace, isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [busy, setBusy] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [applyTpl, setApplyTpl] = useState(null);
  const [applyWs, setApplyWs] = useState("");

  const load = async () => {
    try {
      const [t, w] = await Promise.all([
        api.get("/workspaces/meeting-rules/templates"),
        api.get("/workspaces"),
      ]);
      setTemplates(t.data.templates || []);
      setCategories(t.data.categories || []);
      // Only workspaces where the caller is admin can be apply-targets.
      setWorkspaces((w.data.workspaces || []).filter((x) => x.role === "admin"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return templates.filter((t) => {
      if (cat !== "all" && (t.category || "general") !== cat) return false;
      if (!query) return true;
      if (t.name.toLowerCase().includes(query)) return true;
      if ((t.description || "").toLowerCase().includes(query)) return true;
      return (t.rules || []).some((r) => r.keyword.toLowerCase().includes(query));
    });
  }, [templates, q, cat]);

  const grouped = useMemo(() => {
    const g = { builtin: [], published: [], mine: [] };
    for (const t of filtered) {
      if (t.kind === "builtin") g.builtin.push(t);
      else if (t.is_mine) g.mine.push(t);
      else if (t.is_published) g.published.push(t);
    }
    return g;
  }, [filtered]);

  const applyToWorkspace = async () => {
    if (!applyTpl || !applyWs) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${applyWs}/meeting-rules/apply-template`,
        { template_id: applyTpl.id },
      );
      toast.success(`Applied — ${r.data.added} rules loaded (${r.data.replaced} replaced)`);
      setApplyTpl(null); setApplyWs("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally { setBusy(false); }
  };

  const rateTemplate = async (tpl, rating) => {
    try {
      if (tpl.my_rating === rating) {
        await api.delete(`/workspaces/meeting-rule-templates/${tpl.id}/rate`);
        toast.success("Rating removed");
      } else {
        await api.post(`/workspaces/meeting-rule-templates/${tpl.id}/rate`,
                       { rating });
        toast.success(`Rated ${rating} ★`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rate");
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-sm text-muted-foreground" data-testid="tpl-library-forbidden">
        The Template Library is an admin-only workspace.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="template-library-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Template library</p>
        <h1 className="font-display text-3xl mt-1">Meeting-rule templates</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Browse Ragnes-curated presets plus every template admins have published across the network.
          One-click apply to any workspace you admin.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="tpl-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates, keywords, descriptions…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="tpl-category-filter">
          <button
            onClick={() => setCat("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${cat === "all" ? "bg-sky-500 text-white border-sky-500" : "border-border/60 bg-secondary/20"}`}
            data-testid="tpl-cat-all">
            All ({templates.length})
          </button>
          {categories.map((c) => {
            const n = templates.filter((t) => (t.category || "general") === c).length;
            if (n === 0) return null;
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-sky-500 text-white border-sky-500" : "border-border/60 bg-secondary/20"}`}
                data-testid={`tpl-cat-${c}`}>
                {c} ({n})
              </button>
            );
          })}
        </div>
      </div>

      <TemplateGroup
        title="Ragnes presets"
        eyebrow="Curated by Ragnes"
        icon={<Sparkles className="w-4 h-4 text-sky-500" />}
        templates={grouped.builtin}
        tid="tpl-group-builtin"
        onPreview={setPreviewTpl}
        onApply={(t) => { setApplyTpl(t); setApplyWs(workspace?.id || ""); }}
        onRate={rateTemplate}
      />
      <TemplateGroup
        title="Published by the community"
        eyebrow="Shared across every admin · sorted by rating"
        icon={<Globe className="w-4 h-4 text-emerald-500" />}
        templates={grouped.published}
        tid="tpl-group-published"
        onPreview={setPreviewTpl}
        onApply={(t) => { setApplyTpl(t); setApplyWs(workspace?.id || ""); }}
        onRate={rateTemplate}
        empty="No published community templates yet — publish one from Settings → Meeting rules."
      />
      <TemplateGroup
        title="Your drafts"
        eyebrow="Private to you until published"
        icon={<Lock className="w-4 h-4 text-zinc-500" />}
        templates={grouped.mine}
        tid="tpl-group-mine"
        onPreview={setPreviewTpl}
        onApply={(t) => { setApplyTpl(t); setApplyWs(workspace?.id || ""); }}
        empty="No drafts yet — save the current rules in Settings → Meeting rules."
      />

      {/* Preview modal */}
      <Dialog open={!!previewTpl} onOpenChange={(o) => !o && setPreviewTpl(null)}>
        <DialogContent data-testid="tpl-preview-dialog" className="max-w-lg">
          {previewTpl && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {previewTpl.kind === "builtin" && <Sparkles className="w-4 h-4 text-sky-500" />}
                  {previewTpl.name}
                </DialogTitle>
                <DialogDescription>
                  {previewTpl.description || `${previewTpl.count} rules · ${previewTpl.category || "general"} category`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="tpl-preview-rules">
                {(previewTpl.rules || []).map((r, i) => (
                  <div key={`${r.keyword}-${i}`}
                       className="flex items-center gap-2 text-sm px-3 py-1.5 rounded border border-border/40">
                    <span className="font-mono text-xs flex-1 truncate">"{r.keyword}"</span>
                    <span className="text-muted-foreground text-xs">→</span>
                    <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${KIND_COLORS[r.kind] || "bg-zinc-500/15"}`}>
                      {r.kind}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setPreviewTpl(null)}>Close</Button>
                <Button data-testid="tpl-preview-apply"
                        onClick={() => { setApplyTpl(previewTpl); setApplyWs(workspace?.id || ""); setPreviewTpl(null); }}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Apply this pack
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply-to modal */}
      <Dialog open={!!applyTpl} onOpenChange={(o) => !o && setApplyTpl(null)}>
        <DialogContent data-testid="tpl-apply-dialog">
          {applyTpl && (
            <>
              <DialogHeader>
                <DialogTitle>Apply "{applyTpl.name}"</DialogTitle>
                <DialogDescription>
                  Adds {applyTpl.count} rules to the chosen workspace. Keywords that already exist there will be replaced.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Target workspace</label>
                  <select
                    data-testid="tpl-apply-workspace"
                    value={applyWs}
                    onChange={(e) => setApplyWs(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
                  >
                    {workspaces.length === 0 && <option value="">(no workspaces you admin)</option>}
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={() => setApplyTpl(null)}>Cancel</Button>
                  <Button data-testid="tpl-apply-confirm"
                          onClick={applyToWorkspace}
                          disabled={busy || !applyWs}>
                    {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Apply <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StarRating({ template, onRate, testIdPrefix }) {
  const my = template.my_rating || 0;
  const avg = template.avg_rating || 0;
  const count = template.rating_count || 0;
  const canRate = !!onRate && template.is_published && !template.is_mine;
  return (
    <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-rating`}>
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = canRate ? n <= my : n <= Math.round(avg);
          const active = my === n;
          return (
            <button
              key={n}
              onClick={canRate ? () => onRate(template, n) : undefined}
              disabled={!canRate}
              title={canRate ? (active ? "Click again to remove your rating" : `Rate ${n} star${n === 1 ? "" : "s"}`) : `${avg.toFixed(1)} average · ${count} rating${count === 1 ? "" : "s"}`}
              data-testid={`${testIdPrefix}-star-${n}`}
              className={`p-0.5 transition-transform ${canRate ? "cursor-pointer hover:scale-125" : "cursor-default"}`}
            >
              <Star className={`w-3.5 h-3.5 ${filled ? "text-amber-500 fill-amber-500" : "text-muted-foreground/40"}`} />
            </button>
          );
        })}
      </div>
      <span className="text-[10px] text-muted-foreground font-mono">
        {avg > 0 ? `${avg.toFixed(1)} · ${count}` : (canRate ? "Rate it" : "—")}
      </span>
    </div>
  );
}


function TemplateGroup({ title, eyebrow, icon, templates, tid, onPreview, onApply, onRate, empty }) {
  return (
    <section className="space-y-2" data-testid={tid}>
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{eyebrow}</p>
        <h2 className="text-sm font-medium">{title}</h2>
        <span className="text-xs text-muted-foreground font-mono ml-auto">{templates.length}</span>
      </div>
      {templates.length === 0 ? (
        empty && <p className="text-xs text-muted-foreground italic px-1">{empty}</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t) => (
            <article
              key={t.id}
              data-testid={`tpl-card-${t.id}`}
              className="rounded-lg border border-border/60 p-4 bg-secondary/10 hover:bg-secondary/20 transition-colors flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-500">
                      {t.category || "general"}
                    </span>
                    {t.kind === "custom" && t.is_published && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">
                        published
                      </span>
                    )}
                    {t.kind === "custom" && !t.is_published && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-500">
                        draft
                      </span>
                    )}
                    {typeof t.apply_count === "number" && t.apply_count > 0 && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-500"
                            title="Times this template has been applied">
                        <Users className="w-2.5 h-2.5 inline mr-0.5" /> {t.apply_count}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-mono shrink-0">{t.count}</span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {t.description || (t.owner ? `by ${t.owner.name || t.owner.email}` : "\u00a0")}
              </p>
              {t.kind === "custom" && t.is_published && (
                <StarRating template={t} onRate={onRate}
                            testIdPrefix={`tpl-card-${t.id}`} />
              )}
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => onPreview(t)}
                        data-testid={`tpl-preview-${t.id}`}>
                  Preview
                </Button>
                <Button size="sm" className="flex-1"
                        onClick={() => onApply(t)}
                        data-testid={`tpl-apply-${t.id}`}>
                  Apply <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
